import { useQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import type { SetOptional } from 'type-fest';
import ContentPreviewPopOver from '../../components/ContentPreviewPopOver';
import ExternalBrandLogo from '../../components/ExternalBrandLogo';
import { MentionStatus } from '../../graphql/generated/graphql';
import getExternalBrandLogoProps from '../../integrations/getExternalBrandLogoProps';
import IntegrationMentionCard from '../../integrations/IntegrationMentionCard';
import IntegrationMentionError from '../../integrations/IntegrationMentionError';
import PostPreviewQuillComponent from '../../post/PostPreviewQuillComponent';
import NestableLink from '../../utils/NestableLink';
import useIsPublicView from '../../utils/useIsPublicView';
import type { MentionComponentProps } from '../formats/Mention';
import getEditorMentionUrl from '../utils/getEditorMentionUrl';
import EDITOR_INTEGRATION_MENTIONS from './EditorIntegrationMentions';
import MentionBase from './MentionBase';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

gql(`
  fragment MentionUserFields on MentionUser {
    id
    name
    role
    avatar
  }
`);

const MENTION_FRAGMENT = gql(`
  fragment MentionFields on Mention {
    id
    type
    title
    importable
    url
    icon
    content {
      ... on MentionContentDelta {
        delta
      }
      ... on MentionContentHighlights {
        highlights
      }
    }
    breadcrumbs
    status
    statusDate
    tags {
      id
      name
      color
    }
    preview {
      imageUrl
      content
    }
    participants {
      ...MentionUserFields
    }
    assignee {
      ...MentionUserFields
    }
  }
`);

const GET_MENTION = gql(`
  query getMention($type: MentionType!, $id: ID!) {
    mention(type: $type, id: $id) {
      ...MentionFields
    }
  }
`);

type IntegrationMentionProps = SetOptional<
  MentionComponentProps,
  'updateCache' | 'blot'
>;

const CLOSED_STATUSES = [
  MentionStatus.Closed,
  MentionStatus.Merged,
  MentionStatus.Deleted,
  MentionStatus.Archived,
];

const isMentionClosed = (
  mention?: DocumentType<typeof MENTION_FRAGMENT> | null,
) => {
  return mention?.status ? CLOSED_STATUSES.includes(mention.status) : false;
};

const IntegrationMention = ({
  id,
  type,
  params,
  content,
  mentionText,
  updateCache,
  link,
  showHoverCard,
  selected,
}: IntegrationMentionProps) => {
  const isPublicView = useIsPublicView();
  const apiEnum = EDITOR_INTEGRATION_MENTIONS[type]?.apiEnum;
  const {
    data: { mention } = {},
    error,
    refetch,
  } = useQuery(GET_MENTION, {
    skip: isPublicView || !apiEnum,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    variables: { id, type: apiEnum! },
  });

  const to = getEditorMentionUrl({ type, id, params });
  const text = mentionText || mention?.title || content || to;

  const mentionBaseProps: React.ComponentProps<typeof MentionBase> = {
    type,
    to: link ? to : undefined,
    closed: isMentionClosed(mention),
    icon: <ExternalBrandLogo {...getExternalBrandLogoProps(type)} />,
    content: text,
    truncate: true,
    newTab: true,
  };

  useEffect(() => {
    if (mention) {
      updateCache?.(mention.title);
    }
  }, [updateCache, mention]);

  if (showHoverCard && !selected) {
    if (mention?.preview?.content) {
      return (
        <ContentPreviewPopOver
          hideOnMobile
          allowCrossAxisFlip
          display="inline"
          popOver={
            <NestableLink ignoreInsideLinks to={to}>
              <PostPreviewQuillComponent content={mention.preview.content} />
            </NestableLink>
          }
        >
          <MentionBase {...mentionBaseProps} />
        </ContentPreviewPopOver>
      );
    }
    if (error) {
      return (
        <MentionBase
          popOver={
            <IntegrationMentionError
              type={type}
              error={error}
              refetch={refetch}
            />
          }
          {...mentionBaseProps}
        />
      );
    }
    if (mention) {
      return (
        <MentionBase
          popOver={
            <IntegrationMentionCard url={to} mention={mention} asPopOver />
          }
          {...mentionBaseProps}
        />
      );
    }
  }

  return <MentionBase {...mentionBaseProps} />;
};

export default IntegrationMention;
